import { useCallback, useEffect, useRef, useState } from "react";
import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    Table,
    TableBody,
    TableColumn,
    TableHeader,
    TableCell,
    Spinner,
    TableRow,
    getKeyValue,
    Button,
    Checkbox,
    Slider,
    Select,
    SelectItem,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faTrashCan,
    faUpload,
    faStar as solidStar,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as regularStar } from "@fortawesome/free-regular-svg-icons";
import dayjs from "dayjs";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    FillLayerSpecification,
    GeoJSONSource,
    LineLayerSpecification,
    Popup,
    MapMouseEvent,
    SymbolLayerSpecification,
} from "mapbox-gl";
import { FeatureCollection, MultiPolygon } from "geojson";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import { MZ_COLORS } from "@/lib/map/layers";
import useMap from "@/lib/map/useMap";
import CreationTool from "./CreationTool";
import useAuth from "@/lib/auth/useAuth";
import useConfirm from "@/lib/confirm/useConfirm";
import { useTranslation } from "react-i18next";
import { Resource } from "@/types/user";

function getManagementLayerId(id: number) {
    return `setup-mz-${id}`;
}
function getManagementLayerFill(id: number, isVisible?: boolean) {
    return {
        id: `setup-mz-layer-fill-${id}`,
        type: "fill",
        source: getManagementLayerId(id),
        layout: {
            visibility: isVisible ? "visible" : "none",
        },
        paint: {
            "fill-opacity": 0.8,
        },
    } as FillLayerSpecification;
}

function getManagementLayerLine(id: number, isVisible?: boolean) {
    return {
        id: `setup-mz-layer-line-${id}`,
        type: "line",
        source: getManagementLayerId(id),
        layout: {
            visibility: isVisible ? "visible" : "none",
        },
        paint: {
            "line-color": ["get", "fill-color"],
            "line-width": 3,
        },
    } as LineLayerSpecification;
}

function getManagementLayerSymbol(id: number, isVisible?: boolean) {
    return {
        id: `setup-mz-layer-symbol-${id}`,
        type: "symbol",
        source: getManagementLayerId(id),
        layout: {
            visibility: isVisible ? "visible" : "none",
            "text-field": ["get", "display-value"],
            "text-size": 15,
            "text-stroke-width": 4,
            "text-allow-overlap": true,
        },
    } as SymbolLayerSpecification;
}

function ManagementZones() {
    const { t } = useTranslation();
    const { isAtLeastDetail } = useAuth();
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest, downloadFile, useMutateRequest, axios } =
        useRequest();

    const popupRef = useRef<Popup | null>(null);

    const columns = [
        { key: "name", label: "Name" },
        { key: "view", label: "View" },
        { key: "properties", label: "Properties" },
        { key: "download", label: "Actions" },
    ];

    const managementZonesQuery = useGetRequest<
        {
            id: number;
            name: string;
            is_primary: boolean;
            zones: {
                id: number;
                zone_number: number;
            };
            custom_properties: string[];
        }[]
    >({
        url: `fields/${fieldId}/management_zones/`,
        enabled: !!fieldId,
    });

    const onDownloadManagementZones = useCallback(
        async (layer: { id: number; name: string; is_primary: boolean }) => {
            if (!layer) return;

            await downloadFile(
                `management_zones/${layer.id}/download/`,
                layer.name,
            );
        },
        [downloadFile],
    );

    const uploadZone = useMutateRequest({
        successMessage: t("success.management_zones_uploaded_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_upload_management_zones", {
            ns: "messages",
        }),
    });

    const uploadZoneFile = useCallback(
        async (file: File) => {
            if (!file) return;
            const formData = new FormData();
            formData.append("file", file);
            uploadZone.mutate(
                {
                    url: `fields/${fieldId}/management_zones/`,
                    method: "post",
                    data: formData,
                },
                {
                    onSuccess: () => {
                        managementZonesQuery.refetch();
                    },
                },
            );
        },
        [uploadZone, fieldId, managementZonesQuery],
    );

    const setPrefferedMutate = useMutateRequest({
        successMessage: t(
            "success.preferred_management_zone_set_successfully",
            {
                ns: "messages",
            },
        ),
        errorMessage: t("error.failed_to_set_preferred_management_zone", {
            ns: "messages",
        }),
    });

    const setPreferredManagementZone = useCallback(
        async (managementZoneId: number) => {
            if (!managementZoneId) return;
            setPrefferedMutate.mutate(
                {
                    url: `management_zones/${managementZoneId}/make_preferred/`,
                    method: "put",
                    data: {
                        valid_date: dayjs().format("YYYY-MM-DD"),
                    },
                },
                {
                    onSuccess: () => {
                        managementZonesQuery.refetch();
                    },
                },
            );
        },
        [managementZonesQuery, setPrefferedMutate],
    );
    const inputRef = useRef<HTMLInputElement>(null);

    const { map, mapLoaded } = useMap();

    const createPopup = useCallback(
        (e: MapMouseEvent) => {
            if (!map) return;

            const coordinates = e.lngLat;
            const zoneNumber = e.features?.[0]?.properties?.zone_number;
            const properties = JSON.parse(
                e.features?.[0]?.properties?.custom_properties ?? {},
            );
            if (!properties) return;

            if (popupRef.current) {
                popupRef.current.remove();
            }

            const popupContent = `
            <div style="padding: 8px; font-family: system-ui, sans-serif;">
                <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
                    Zone ${zoneNumber}
                </h4>
                <table style="
                    width: 100%; 
                    font-size: 12px; 
                    border-collapse: collapse;
                    line-height: 1.4;
                ">
                    <tbody>
                        ${Object.entries(properties)
                            .filter(([key]) => key !== "fill-color")
                            .map(
                                ([key, value]) => `
                                <tr>
                                    <td style="
                                        border: 1px solid #ddd;
                                        padding: 4px; 
                                        font-weight: 600; 
                                        vertical-align: top;
                                        width: 40%;
                                    ">
                                        ${key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}:
                                    </td>
                                    <td style="
                                        border: 1px solid #ddd;
                                        padding: 4px; 
                                        vertical-align: top;
                                        word-break: break-word;
                                    ">
                                        ${value}
                                    </td>
                                </tr>
                            `,
                            )
                            .join("")}
                    </tbody>
                </table>
            </div>
        `;

            popupRef.current = new Popup({
                closeButton: true,
                closeOnClick: true,
                className: "management-zone-popup",
            })
                .setLngLat(coordinates)
                .setHTML(popupContent)
                .addTo(map);
        },
        [map],
    );

    const removePopup = useCallback(() => {
        if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
        }
    }, []);

    const addPopupListeners = useCallback(
        (layerId: number) => {
            if (!map) return;

            const fillLayerId = getManagementLayerFill(layerId).id;

            map.on("mouseenter", fillLayerId, () => {
                map.getCanvas().style.cursor = "pointer";
            });
            map.on("mouseleave", fillLayerId, () => {
                map.getCanvas().style.cursor = "";
            });
            map.on("click", fillLayerId, createPopup);
        },
        [map, createPopup],
    );

    const removePopupListeners = useCallback(
        (layerId: number) => {
            if (!map) return;

            const fillLayerId = getManagementLayerFill(layerId).id;

            map.off("mouseenter", fillLayerId, createPopup);
            map.off("mouseleave", fillLayerId, removePopup);
            removePopup();
        },
        [map, createPopup, removePopup],
    );

    const [selectedProperties, setSelectedProperties] = useState<
        Record<string, string[]>
    >({});
    const [visibilityState, setVisibilityState] = useState<
        Record<string, boolean>
    >({});

    const onValueChanged = useCallback(
        (isChecked: boolean, layerId: number, displayProperty: string) => {
            if (!map || !mapLoaded) return;
            if (isChecked) {
                axios
                    .get<{
                        zones: FeatureCollection<MultiPolygon>;
                    }>(`management_zones/${layerId}/`)
                    .then((res) => {
                        if (
                            displayProperty === "zone_number" ||
                            (displayProperty &&
                                typeof res.data.zones.features[0]?.properties
                                    ?.custom_properties[displayProperty] ===
                                    "string")
                        ) {
                            const uniqueValues = Array.from(
                                new Set(
                                    res.data.zones.features.map((feature) =>
                                        displayProperty === "zone_number"
                                            ? feature.properties?.zone_number
                                            : feature.properties
                                                  ?.custom_properties[
                                                  displayProperty
                                              ],
                                    ),
                                ),
                            ).filter(
                                (val) => val !== undefined && val !== null,
                            );
                            addUpdateGeojsonSource(
                                map,
                                getManagementLayerId(layerId),
                                {
                                    type: "geojson",
                                    data: {
                                        type: "FeatureCollection",
                                        features: res.data.zones.features.map(
                                            (feature) => {
                                                const displayValue =
                                                    displayProperty ===
                                                    "zone_number"
                                                        ? feature.properties
                                                              ?.zone_number
                                                        : feature.properties
                                                              ?.custom_properties[
                                                              displayProperty
                                                          ];
                                                const colorIndex =
                                                    uniqueValues.indexOf(
                                                        displayValue,
                                                    );
                                                return {
                                                    ...feature,
                                                    properties: {
                                                        ...feature.properties,
                                                        "fill-color":
                                                            MZ_COLORS[
                                                                colorIndex %
                                                                    MZ_COLORS.length
                                                            ],
                                                        "display-value":
                                                            displayValue ??
                                                            null,
                                                    },
                                                };
                                            },
                                        ),
                                    },
                                    tolerance: 0,
                                },
                            );
                            addUpdateGeojsonLayers(map, [
                                getManagementLayerFill(layerId, true),
                                getManagementLayerLine(layerId, true),
                                getManagementLayerSymbol(layerId, true),
                            ]);
                            map.setPaintProperty(
                                getManagementLayerFill(layerId).id,
                                "fill-color",
                                ["get", "fill-color"],
                            );
                        } else {
                            addUpdateGeojsonSource(
                                map,
                                getManagementLayerId(layerId),
                                {
                                    type: "geojson",
                                    data: {
                                        type: "FeatureCollection",
                                        features: res.data.zones.features.map(
                                            (feature) => ({
                                                ...feature,
                                                properties: {
                                                    ...feature.properties,
                                                    "display-value":
                                                        feature.properties
                                                            ?.custom_properties[
                                                            displayProperty
                                                        ] ?? null,
                                                },
                                            }),
                                        ),
                                    },
                                    tolerance: 0,
                                },
                            );
                            addUpdateGeojsonLayers(map, [
                                getManagementLayerFill(layerId, true),
                                getManagementLayerLine(layerId, true),
                                getManagementLayerSymbol(layerId, true),
                            ]);
                            const displayValues = res.data.zones.features
                                .map(
                                    (feature) =>
                                        feature.properties?.custom_properties[
                                            displayProperty
                                        ],
                                )
                                .filter(
                                    (val) => val !== undefined && val !== null,
                                );
                            map.setPaintProperty(
                                getManagementLayerFill(layerId).id,
                                "fill-color",
                                [
                                    "interpolate",
                                    ["linear"],
                                    ["get", "display-value"],
                                    Math.min(...displayValues),
                                    "#ff0000",
                                    displayValues.reduce(
                                        (acc, val) => acc + val,
                                        0,
                                    ) / displayValues.length,
                                    "#ffff00",
                                    Math.max(...displayValues),
                                    "#00ff00",
                                ],
                            );
                        }
                        addPopupListeners(layerId);
                    });
            } else {
                removeLayersIfExists(map, [
                    getManagementLayerFill(layerId, false).id,
                    getManagementLayerLine(layerId, false).id,
                    getManagementLayerSymbol(layerId, false).id,
                ]);
                removeSourcesIfExists(map, [getManagementLayerId(layerId)]);
                removePopupListeners(layerId);
            }
        },
        [axios, map, mapLoaded, addPopupListeners, removePopupListeners],
    );

    useEffect(() => {
        if (!map || !mapLoaded) return;
        Object.entries(visibilityState).forEach(([layerId, isVisible]) => {
            const selectedProperty =
                selectedProperties[layerId] === undefined
                    ? "zone_number"
                    : selectedProperties[layerId][0];
            onValueChanged(isVisible, Number(layerId), selectedProperty);
        });
    }, [map, mapLoaded, visibilityState, selectedProperties, onValueChanged]);

    const onOpacityChanged = useCallback(
        (opacity: number, layerId: number) => {
            if (!map || !mapLoaded) return;
            const source = map.getSource(
                getManagementLayerId(layerId),
            ) as GeoJSONSource;
            if (!source) return;

            map.setPaintProperty(
                getManagementLayerFill(layerId).id,
                "fill-opacity",
                opacity,
            );
            map.setPaintProperty(
                getManagementLayerLine(layerId).id,
                "line-opacity",
                opacity,
            );
        },
        [map, mapLoaded],
    );

    useEffect(() => {
        if (!map || !mapLoaded) return;
        return () => {
            managementZonesQuery.data?.forEach((layer) => {
                removeSourcesIfExists(map, [
                    getManagementLayerId(Number(layer.id)),
                ]);
                removeLayersIfExists(map, [
                    getManagementLayerFill(Number(layer.id)).id,
                    getManagementLayerLine(Number(layer.id)).id,
                    getManagementLayerSymbol(Number(layer.id)).id,
                ]);
                removePopupListeners(Number(layer.id));
            });
        };
    }, [map, mapLoaded, managementZonesQuery.data, removePopupListeners]);

    const triggerMutate = useMutateRequest({
        successMessage: t("success.run_triggered_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_trigger_run", {
            ns: "messages",
        }),
    });

    const { showConfirm } = useConfirm();

    const deleteRequest = useMutateRequest({
        successMessage: t("success.management_zone_deleted_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_delete_management_zone", {
            ns: "messages",
        }),
    });

    const onDeleteManagementZone = useCallback(
        (managementZoneId: number) => {
            showConfirm({
                title: t("management_zones.delete_management_zone_title", {
                    ns: "setup",
                }),
                description: t(
                    "management_zones.delete_management_zone_description",
                    {
                        ns: "setup",
                    },
                ),
                color: "danger",
                onConfirm: async () => {
                    await deleteRequest.mutateAsync(
                        {
                            url: `management_zones/${managementZoneId}/`,
                            method: "delete",
                        },
                        {
                            onSuccess: () => {
                                managementZonesQuery.refetch();
                            },
                        },
                    );
                },
            });
        },
        [showConfirm, t, deleteRequest, managementZonesQuery],
    );

    return (
        <div className="flex flex-col gap-2">
            <Button
                size="sm"
                color="primary"
                startContent={<FontAwesomeIcon icon={faUpload} />}
                isLoading={uploadZone.isPending}
                isDisabled={uploadZone.isPending}
                endContent={
                    <input
                        type="file"
                        hidden
                        ref={inputRef}
                        onChange={(e) => {
                            if (e.target.files?.[0]) {
                                uploadZoneFile(e.target.files[0]);
                            }
                        }}
                    />
                }
                onPress={() => {
                    inputRef.current?.click();
                }}
            >
                {t("management_zones.upload_management_zone", {
                    ns: "setup",
                })}
            </Button>
            <Table
                isHeaderSticky
                isVirtualized
                maxTableHeight={200}
                aria-label="trendline"
                classNames={{
                    th: "px-[0.25rem]",
                    td: "px-[0.25rem]",
                }}
            >
                <TableHeader columns={columns}>
                    {(column) => (
                        <TableColumn key={column.key} allowsSorting>
                            {column.label}
                        </TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    items={sortArrayByKey(
                        managementZonesQuery.data ?? [],
                        "name",
                    )}
                    emptyContent={t(
                        "management_zones.no_management_zones_found",
                        {
                            ns: "setup",
                        },
                    )}
                    isLoading={managementZonesQuery.isLoading}
                    loadingContent={<Spinner label="Loading..." />}
                >
                    {(item) => (
                        <TableRow
                            key={item.id}
                            className="hover:bg-gray-100 rounded-lg"
                        >
                            {(columnKey) => {
                                if (columnKey === "view") {
                                    return (
                                        <TableCell className="text-xs  min-w-[8rem]">
                                            <div className="flex items-center gap-1 w-full">
                                                <Checkbox
                                                    size="sm"
                                                    aria-label="View Layer"
                                                    isSelected={
                                                        visibilityState[item.id]
                                                    }
                                                    onValueChange={(
                                                        isChecked,
                                                    ) => {
                                                        setVisibilityState(
                                                            (prev) => ({
                                                                ...prev,
                                                                [item.id]:
                                                                    isChecked,
                                                            }),
                                                        );
                                                    }}
                                                />
                                                <Slider
                                                    size="sm"
                                                    defaultValue={0.8}
                                                    minValue={0}
                                                    aria-label="Opacity"
                                                    maxValue={1}
                                                    step={0.01}
                                                    onChange={(e) => {
                                                        onOpacityChanged(
                                                            e as number,
                                                            Number(item.id),
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "properties") {
                                    return (
                                        <TableCell>
                                            <Select
                                                className="min-w-[8rem]"
                                                size="sm"
                                                selectionMode="single"
                                                defaultSelectedKeys={[
                                                    "zone_number",
                                                ]}
                                                selectedKeys={
                                                    selectedProperties[item.id]
                                                }
                                                onSelectionChange={(keys) => {
                                                    const selectedArray =
                                                        Array.from(
                                                            keys,
                                                        ) as string[];
                                                    setSelectedProperties(
                                                        (prev) => ({
                                                            ...prev,
                                                            [item.id]:
                                                                selectedArray,
                                                        }),
                                                    );
                                                }}
                                            >
                                                {item.custom_properties
                                                    .concat("zone_number")
                                                    .map((property) => (
                                                        <SelectItem
                                                            key={property}
                                                        >
                                                            {property
                                                                .replace(
                                                                    /_/g,
                                                                    " ",
                                                                )
                                                                .replace(
                                                                    /\b\w/g,
                                                                    (l) =>
                                                                        l.toUpperCase(),
                                                                )}
                                                        </SelectItem>
                                                    ))}
                                            </Select>
                                        </TableCell>
                                    );
                                }

                                if (columnKey === "download") {
                                    return (
                                        <TableCell className="text-xs flex flex-row">
                                            {isAtLeastDetail(
                                                Resource.MANAGEMENT_ZONES,
                                            ) && (
                                                <Button
                                                    isIconOnly
                                                    color={
                                                        item.is_primary
                                                            ? "primary"
                                                            : "default"
                                                    }
                                                    variant="light"
                                                    size="sm"
                                                    aria-label="Set Preferred Management Zone"
                                                    onPress={() =>
                                                        setPreferredManagementZone(
                                                            item.id,
                                                        )
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={
                                                            item.is_primary
                                                                ? solidStar
                                                                : regularStar
                                                        }
                                                        color={
                                                            item.is_primary
                                                                ? "#FFD700"
                                                                : "black"
                                                        }
                                                    />
                                                </Button>
                                            )}
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                color="primary"
                                                aria-label="Download Management Zone"
                                                onPress={() =>
                                                    onDownloadManagementZones(
                                                        item,
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                />
                                            </Button>
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                color="danger"
                                                aria-label="Delete"
                                                onPress={() =>
                                                    onDeleteManagementZone(
                                                        item.id,
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTrashCan}
                                                />
                                            </Button>
                                        </TableCell>
                                    );
                                }
                                return (
                                    <TableCell className="text-xs">
                                        {getKeyValue(item, columnKey)}
                                    </TableCell>
                                );
                            }}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {isAtLeastDetail(Resource.MANAGEMENT_ZONES) && (
                <>
                    <CreationTool />
                    <div className="flex flex-row gap-2 w-full">
                        <Button
                            color="secondary"
                            size="sm"
                            onPress={() =>
                                triggerMutate.mutate({
                                    url: `fields/${fieldId}/image_based_zones/`,
                                    method: "get",
                                })
                            }
                        >
                            {t("management_zones.create_image_based_zones", {
                                ns: "setup",
                            })}
                        </Button>
                        <Button
                            color="secondary"
                            size="sm"
                            onPress={() =>
                                triggerMutate.mutate({
                                    url: `fields/${fieldId}/retrieve_soil_map/`,
                                    method: "get",
                                })
                            }
                        >
                            {t("management_zones.trigger_soils_run", {
                                ns: "setup",
                            })}
                        </Button>
                        <Button
                            color="secondary"
                            size="sm"
                            onPress={() => {
                                triggerMutate.mutate({
                                    url: `fields/${fieldId}/retrieve_slope_map/`,
                                    method: "get",
                                });
                            }}
                        >
                            {t("management_zones.trigger_slope_elevation_run", {
                                ns: "setup",
                            })}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

export default ManagementZones;
